/**
 * The `clipRef` widget (Anim to play): a menu of the clips the entity's model
 * has, from the widget environment (the content index's asset catalog), so
 * visitors see what a model can play. A saved name the model lacks stays
 * selected and is marked missing, rather than silently changing.
 */
import type { PropType } from "../../world/schema/propTypes";
import { FieldError, fieldAria } from "./fieldAria";
import { isMixed, MIXED_OPTION, MixedOption } from "./mixed";
import type { WidgetProps } from "./types";
import { useWidgetEnvironment } from "./WidgetEnvironment";

export function ClipWidget({ id, label, prop, value, onChange, onCommit, error }: WidgetProps) {
  const { source } = prop as Extract<PropType, { type: "clipRef" }>;
  const clips = useWidgetEnvironment().clipOptions(source);
  const current = typeof value === "string" ? value : "";
  const missing = current !== "" && !clips.includes(current);

  return (
    <span className="kn-field">
      <select
        id={id}
        className="kn-input kn-select"
        aria-label={label}
        value={isMixed(value) ? MIXED_OPTION : current}
        disabled={prop.readonly === true}
        {...fieldAria(id, error)}
        onChange={(event) => {
          onChange(event.target.value);
          onCommit();
        }}
      >
        <MixedOption mixed={isMixed(value)} />
        <option value="">(First clip)</option>
        {clips.map((clip) => (
          <option key={clip} value={clip}>
            {clip}
          </option>
        ))}
        {missing && <option value={current}>{current} (missing)</option>}
      </select>
      <FieldError id={id} error={error} />
    </span>
  );
}
